import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { usePatientAllPrescriptions } from '../../../../../../hooks/queries/usePatientPrescriptions';
import { usePatientRecord } from '../../../../../../hooks/queries/usePatientRecord';
import { usePharmacyStore } from '../../../../../../stores/pharmacyStore';
import {
  fetchPhoxDocumentDownloadUrl,
  listPhoxDocuments,
  type PhoxPatientDocument,
} from '../../../../../../services/patientDocumentsService';
import { getPharmacyTodayString } from '../../../../../../utils/timeUtils';
import { formatDateForDisplay } from '../../../../../../utils/formatters';
import { usePatient360ReadOnly } from '../../../context/Patient360ReadOnlyContext';
import {
  COVERAGE_LABELS,
  DEFAULT_BUFFER_DAYS,
  DESTINATION_LABELS,
  USED_SUPPLY_CAP_DAYS,
  VACATION_RULES_VERSION,
  addDaysTo,
  benefitYearOf,
  planVacationSupply,
  type Coverage,
  type Destination,
  type VacationMedInput,
  type VacationRowResult,
  type VacationTripInput,
} from './vacationRules';
import { vacationRowsFromRx } from './vacationRows';
import { buildVacationFormRequest, buildVacationTask, canRequest, formGate } from './vacationRequest';
import { useVacationCoverageDefault } from './useVacationCoverageDefault';
import { VacationSupplyRequestDialog } from './VacationSupplyRequestDialog';
import { VacationSupplyFormDialog, type VacationSupplyFormCreated } from './VacationSupplyFormDialog';

interface VacationSupplyViewProps {
  /** The patient's pharmacy (the record and task pharmacy). */
  pharmacyId: string;
  /** The composite `<pharmacyId>_<patientNumber>`. */
  patientId: string;
  /** The Rx the menu was opened on: ticked to start with. */
  initialRxNumber?: string | null;
  onBack: () => void;
}

/** What staff typed for a row's on hand: the quantity, or days (then qty = days × per day). */
type OnHandEdit = { kind: 'qty'; raw: string } | { kind: 'days'; raw: string };

const COVERAGES: Coverage[] = ['private', 'odb', 'tdp', 'cash'];

const headingSx = { fontWeight: 600 } as const;

const parseNumber = (raw: string): number | null => {
  if (raw.trim() === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

const shortNumber = (n: number | null): string => (n == null ? '—' : String(Number(n.toFixed(2))));
const showDate = (d: string | null): string => (d ? formatDateForDisplay(d) : '—');

function statusChip(row: VacationRowResult): React.ReactNode {
  switch (row.status) {
    case 'not_selected':
      return <Typography variant="body2" color="text.secondary">Not included</Typography>;
    case 'cannot_calculate':
      return (
        <Tooltip title={row.reason ?? ''}>
          <Chip size="small" color="warning" variant="outlined" label="Can't calculate" />
        </Tooltip>
      );
    case 'enough_on_hand':
      return <Chip size="small" color="success" variant="outlined" label="Enough on hand" />;
    case 'short':
      return (
        <Tooltip title={row.reason ?? ''}>
          <Chip size="small" color="error" variant="outlined" label={`Short by ${row.shortDays} ${row.shortDays === 1 ? 'day' : 'days'}`} />
        </Tooltip>
      );
    case 'fill':
      return (
        <Chip
          size="small"
          color="primary"
          variant="outlined"
          label={row.bridgeQty > 0 ? 'Bridge, then fill' : 'Fill'}
        />
      );
  }
}

/**
 * PRX-130 — the vacation supply calculator, a sub-view of the Patient 360
 * Prescriptions tab (Rafik's wireframe). Rows come from the tab's own
 * all-prescriptions read; the coverage defaults from the plans on file; the
 * rules are `vacationRules.ts`.
 *
 * The only write depends on the patient's pharmacy system (WP-10, Rafik's
 * answers of 7 Oct 2026). Fillware: a form for the patient's signature saved
 * to the patient's documents, a 7-day default buffer, and "already used"
 * limits the supply to 100 days without refusing. Any other system, or one
 * that could not be read: the request task, a buffer of 0 and the refusal, as
 * first built.
 */
export const VacationSupplyView: React.FC<VacationSupplyViewProps> = ({
  pharmacyId,
  patientId,
  initialRxNumber,
  onBack,
}) => {
  const today = getPharmacyTodayString();
  const { readOnly, message: readOnlyMessage } = usePatient360ReadOnly();
  const rxQuery = usePatientAllPrescriptions(patientId, 'active');
  const coverageDefault = useVacationCoverageDefault(pharmacyId, patientId);
  // The same cached read `useVacationCoverageDefault` makes.
  const record = usePatientRecord(pharmacyId, patientId);
  const fillware = record.data?.databaseType === 'fillware';
  // A failed read is not "not Fillware". The pharmacy list the app already holds
  // is asked instead: a pharmacy it knows is not Fillware keeps the task flow as
  // before; a Fillware one, or one it cannot tell, creates nothing until the read succeeds.
  const pharmacies = usePharmacyStore((state) => state.pharmacies);
  const listedSystem = pharmacies.find((p) => p.id === pharmacyId)?.databaseType ?? null;
  const recordFailed = record.isError && !record.data && (!listedSystem || listedSystem === 'fillware');

  const [departureDate, setDepartureDate] = useState('');
  const [returnDate, setReturnDate] = useState('');
  // Null until staff type: the default then follows the pharmacy system.
  const [bufferEdit, setBufferEdit] = useState<string | null>(null);
  const bufferRaw = bufferEdit ?? String(fillware ? DEFAULT_BUFFER_DAYS : 0);
  const [destination, setDestination] = useState<Destination | null>(null);
  const [coverageChoice, setCoverageChoice] = useState<Coverage | null>(null);
  const [maxSingleFillRaw, setMaxSingleFillRaw] = useState('');
  const [travelLetterOnFile, setTravelLetterOnFile] = useState(false);
  const [overrideOnFile, setOverrideOnFile] = useState(false);
  const [odbVacationUsed, setOdbVacationUsed] = useState(false);
  const [selected, setSelected] = useState<ReadonlySet<string>>(
    () => new Set(initialRxNumber ? [initialRxNumber] : []),
  );
  const [edits, setEdits] = useState<ReadonlyMap<string, OnHandEdit>>(new Map());
  const [requestOpen, setRequestOpen] = useState(false);
  const [created, setCreated] = useState<string | null>(null);
  const [formCreated, setFormCreated] = useState<VacationSupplyFormCreated | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);

  // WP-10: the forms already saved, to show on the prescriptions they cover.
  // Its own cache entry: this read asks for the Rx numbers, the Documents tab's plain read does not.
  const documents = useQuery({
    queryKey: ['patient-documents', patientId, 'with-rx-numbers'],
    queryFn: () => listPhoxDocuments(patientId, { withRxNumbers: true }),
    enabled: fillware,
  });
  const formByRx = useMemo(() => {
    const newest = new Map<string, PhoxPatientDocument>();
    for (const doc of documents.data ?? []) {
      if (doc.documentType !== 'vacation_supply_form') continue;
      for (const rxNumber of doc.rxNumbers ?? []) {
        const seen = newest.get(rxNumber);
        if (!seen || doc.createdAt > seen.createdAt) newest.set(rxNumber, doc);
      }
    }
    return newest;
  }, [documents.data]);

  const openDocument = async (doc: PhoxPatientDocument) => {
    setOpenError(null);
    try {
      const { url } = await fetchPhoxDocumentDownloadUrl(patientId, doc.id);
      window.open(url, '_blank', 'noopener');
    } catch {
      setOpenError("Couldn't open the form. Open it from the Documents tab.");
    }
  };

  const rxRows = useMemo(() => vacationRowsFromRx(rxQuery.data ?? [], today), [rxQuery.data, today]);

  const coverage = coverageChoice ?? coverageDefault.coverage;
  const trip: VacationTripInput = {
    today,
    departureDate: departureDate || null,
    returnDate: returnDate || null,
    bufferDays: parseNumber(bufferRaw),
    destination,
    coverage,
    maxSingleFillDays: parseNumber(maxSingleFillRaw),
    travelLetterOnFile,
    overrideOnFile,
    odbVacationUsed,
    usedSupplyPolicy: fillware ? 'cap' : 'refuse',
  };

  const meds: VacationMedInput[] = rxRows.map((r) => {
    const edit = edits.get(r.rxNumber);
    let onHandQty = r.estimatedOnHandQty;
    if (edit?.kind === 'qty') onHandQty = parseNumber(edit.raw);
    if (edit?.kind === 'days') {
      const days = parseNumber(edit.raw);
      onHandQty = days != null && r.perDay != null ? days * r.perDay : null;
    }
    return {
      rxNumber: r.rxNumber,
      drugName: r.drugName,
      selected: selected.has(r.rxNumber),
      lastFillDate: r.lastFillDate,
      daysSupply: r.daysSupply,
      perDay: r.perDay,
      onHandQty,
    };
  });
  const plan = planVacationSupply(trip, meds);
  const requestGate = canRequest(plan, readOnly, readOnlyMessage);
  const benefitYear = benefitYearOf(today);

  const setEdit = (rxNumber: string, edit: OnHandEdit) =>
    setEdits((prev) => new Map(prev).set(rxNumber, edit));
  const toggle = (rxNumber: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(rxNumber)) next.delete(rxNumber);
      else next.add(rxNumber);
      return next;
    });
  // The menu was opened on an Rx the active read does not return (inactive or held).
  const openedOnMissingRx =
    !!initialRxNumber && !rxQuery.isLoading && !rxQuery.isError && !rxRows.some((r) => r.rxNumber === initialRxNumber);
  const allTicked = rxRows.length > 0 && rxRows.every((r) => selected.has(r.rxNumber));

  const reload = () => {
    setEdits(new Map());
    rxQuery.refetch();
  };

  const coverageSourceText = coverageDefault.pending
    ? coverageDefault.source
    : coverageDefault.coverage
      ? `Default ${COVERAGE_LABELS[coverageDefault.coverage]}: ${coverageDefault.source}`
      : `No default: ${coverageDefault.source}`;

  const patientName: string | null =
    (rxQuery.data ?? []).map((rx: any) => String(rx?.PatientName ?? '').trim()).find(Boolean) || null;

  // Until the pharmacy system is read, neither a task nor a form is offered.
  const gate: { ok: true } | { ok: false; reason: string } = record.isLoading
    ? { ok: false, reason: "Reading the patient's pharmacy system" }
    : recordFailed
      ? { ok: false, reason: "The patient's pharmacy system could not be read" }
      : requestGate.ok && fillware && plan.status === 'ok'
        ? formGate(plan)
        : requestGate;

  return (
    <Box data-testid="vacation-supply-view">
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ pb: 1.5 }} flexWrap="wrap">
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={onBack}>
          Prescriptions
        </Button>
        <Typography variant="h6" component="h2" sx={headingSx}>
          Vacation supply
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          Rules version {VACATION_RULES_VERSION}
        </Typography>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={headingSx}>
              Trip
            </Typography>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              <TextField
                size="small"
                type="date"
                label="Departure"
                value={departureDate}
                onChange={(e) => setDepartureDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: today }}
              />
              <TextField
                size="small"
                type="date"
                label="Return"
                value={returnDate}
                onChange={(e) => setReturnDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: departureDate || today }}
              />
              <TextField
                size="small"
                type="number"
                label="Buffer days"
                value={bufferRaw}
                onChange={(e) => setBufferEdit(e.target.value)}
                inputProps={{ min: 0, step: 1 }}
                sx={{ width: 120 }}
              />
            </Stack>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Destination
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={destination}
                onChange={(_e, value) => value && setDestination(value)}
                aria-label="Destination"
              >
                <ToggleButton value="in_ontario">{DESTINATION_LABELS.in_ontario}</ToggleButton>
                <ToggleButton value="outside_ontario">{DESTINATION_LABELS.outside_ontario}</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Stack>

          <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={headingSx}>
              Coverage
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="flex-start" flexWrap="wrap" useFlexGap>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="vacation-coverage">Coverage</InputLabel>
                <Select
                  labelId="vacation-coverage"
                  label="Coverage"
                  value={coverage ?? ''}
                  onChange={(e) => setCoverageChoice(e.target.value as Coverage)}
                >
                  {COVERAGES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {COVERAGE_LABELS[c]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                type="number"
                label="Max single fill (days)"
                value={maxSingleFillRaw}
                onChange={(e) => setMaxSingleFillRaw(e.target.value)}
                inputProps={{ min: 1, step: 1 }}
                helperText="Leave empty for no cap"
                sx={{ width: 180 }}
              />
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              {coverageDefault.pending && <CircularProgress size={14} />}
              <Typography variant="body2" color="text.secondary" data-testid="vacation-coverage-source">
                {coverageSourceText}
              </Typography>
              {coverageChoice && coverageDefault.coverage && coverageChoice !== coverageDefault.coverage && (
                <Button size="small" onClick={() => setCoverageChoice(null)}>
                  Use default
                </Button>
              )}
            </Stack>
            <Stack>
              <FormControlLabel
                control={<Checkbox size="small" checked={travelLetterOnFile} onChange={(e) => setTravelLetterOnFile(e.target.checked)} />}
                label="Travel letter or insurance on file"
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={overrideOnFile} onChange={(e) => setOverrideOnFile(e.target.checked)} />}
                label="Plan override on file"
              />
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <FormControlLabel
                  control={<Checkbox size="small" checked={odbVacationUsed} onChange={(e) => setOdbVacationUsed(e.target.checked)} />}
                  label={`ODB vacation supply already used this benefit year (${formatDateForDisplay(benefitYear.start)} to ${formatDateForDisplay(benefitYear.end)})`}
                />
                {odbVacationUsed && coverage === 'odb' &&
                  (fillware ? (
                    <Chip size="small" color="warning" label={`Limited to ${USED_SUPPLY_CAP_DAYS} days`} />
                  ) : (
                    <Chip size="small" color="error" label="A second early fill will reject" />
                  ))}
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid="vacation-result">
        {plan.status === 'cannot_calculate' ? (
          <Alert severity="info">
            Can't calculate yet: {plan.reasons.join('. ')}.
          </Alert>
        ) : (
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
              <Box>
                <Typography variant="body2" color="text.secondary">Recommended fill date</Typography>
                <Typography variant="h6" data-testid="vacation-fill-date">
                  {plan.recommendedFillDate ? formatDateForDisplay(plan.recommendedFillDate) : 'No fill needed'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Bridge fill</Typography>
                <Typography variant="h6" data-testid="vacation-bridge">
                  {plan.bridgeNeeded ? 'Needed' : 'Not needed'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Fill</Typography>
                <Typography variant="h6">
                  {plan.rows.filter((r) => r.fillQty > 0).length} of {plan.rows.filter((r) => r.selected).length} ticked
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Supply must last through</Typography>
                <Typography variant="h6">{formatDateForDisplay(addDaysTo(plan.needEndDate, -1))}</Typography>
              </Box>
            </Stack>
            {plan.rules.length > 0 && (
              <Box data-testid="vacation-rules">
                {plan.rules.map((rule) => (
                  <Typography key={rule} variant="body2" color="text.secondary">
                    {rule}
                  </Typography>
                ))}
              </Box>
            )}
            {plan.warnings.map((w) => (
              <Alert key={w.code} severity={w.severity}>
                {w.message}
              </Alert>
            ))}
          </Stack>
        )}
      </Paper>

      <Stack direction="row" spacing={1.5} sx={{ pb: 1 }} alignItems="center" flexWrap="wrap">
        <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={reload} disabled={rxQuery.isFetching}>
          Reload from prescriptions
        </Button>
        <Tooltip title={gate.ok ? '' : gate.reason}>
          <span>
            <Button size="small" variant="contained" disabled={!gate.ok} onClick={() => setRequestOpen(true)}>
              {fillware ? 'Create vacation supply form' : 'Create vacation supply request'}
            </Button>
          </span>
        </Tooltip>
        {!gate.ok && (
          <Typography variant="body2" color="text.secondary" data-testid="vacation-request-gate">
            {gate.reason}
          </Typography>
        )}
      </Stack>
      {recordFailed && (
        <Alert
          severity="error"
          sx={{ mb: 1 }}
          action={
            <Button color="inherit" size="small" onClick={() => record.refetch()}>
              Retry
            </Button>
          }
        >
          The patient's pharmacy system could not be read. Nothing can be created until it is.
        </Alert>
      )}
      {openedOnMissingRx && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Rx {initialRxNumber} is not active and is not in the calculator.
        </Alert>
      )}
      {created && (
        <Alert severity="success" sx={{ mb: 1 }} onClose={() => setCreated(null)}>
          Task created: {created}
        </Alert>
      )}
      {formCreated && (
        <Alert
          severity={formCreated.proof === 'failed' ? 'warning' : 'success'}
          sx={{ mb: 1 }}
          onClose={() => setFormCreated(null)}
          data-testid="vacation-form-created"
        >
          {formCreated.alreadyExisted
            ? 'This form was already saved today. No second form was made: '
            : "Form saved to the patient's documents for signature: "}
          {formCreated.document.title}.
          {formCreated.proof === 'saved' && ' Proof of travel saved.'}
          {formCreated.proof === 'failed' &&
            ' The proof of travel could not be uploaded. Add it from the Documents tab.'}
          {formCreated.earlierFormToday &&
            ' An earlier form for this trip was saved today. Archive it in the Documents tab if it is no longer correct.'}{' '}
          <Button color="inherit" size="small" onClick={() => openDocument(formCreated.document)}>
            Open form
          </Button>
        </Alert>
      )}
      {openError && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setOpenError(null)}>
          {openError}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label="Vacation supply medications">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allTicked}
                  indeterminate={!allTicked && selected.size > 0}
                  onChange={() => setSelected(allTicked ? new Set() : new Set(rxRows.map((r) => r.rxNumber)))}
                  inputProps={{ 'aria-label': 'Tick every medication' }}
                />
              </TableCell>
              <TableCell sx={headingSx}>Medication</TableCell>
              <TableCell sx={headingSx}>Per day</TableCell>
              <TableCell sx={headingSx}>Qty on hand</TableCell>
              <TableCell sx={headingSx}>Days on hand</TableCell>
              <TableCell sx={headingSx}>Last fill</TableCell>
              <TableCell sx={headingSx}>Days supply</TableCell>
              <TableCell sx={headingSx}>Runs out</TableCell>
              <TableCell sx={headingSx}>Earliest refill</TableCell>
              <TableCell sx={headingSx}>Status</TableCell>
              <TableCell sx={headingSx}>Bridge qty</TableCell>
              <TableCell sx={headingSx}>Fill qty</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rxQuery.isLoading ? (
              <TableRow>
                <TableCell colSpan={12} align="center">
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            ) : rxQuery.isError ? (
              <TableRow>
                <TableCell colSpan={12}>
                  <Alert severity="error">Couldn't load the prescriptions. Reload to try again.</Alert>
                </TableCell>
              </TableRow>
            ) : plan.rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12}>
                  <Typography variant="body2" color="text.secondary">No active prescriptions.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              plan.rows.map((row) => {
                const edit = edits.get(row.rxNumber);
                const qtyValue =
                  edit?.kind === 'qty' ? edit.raw : row.onHandQty != null ? String(Number(row.onHandQty.toFixed(2))) : '';
                const daysValue = edit?.kind === 'days' ? edit.raw : row.daysOnHand != null ? String(row.daysOnHand) : '';
                return (
                  <TableRow key={row.rxNumber} hover data-testid={`vacation-row-${row.rxNumber}`}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={row.selected}
                        onChange={() => toggle(row.rxNumber)}
                        inputProps={{ 'aria-label': `Include Rx ${row.rxNumber}` }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.drugName || 'No drug name'}</Typography>
                      <Typography variant="caption" color="text.secondary">Rx {row.rxNumber}</Typography>
                      {formByRx.has(row.rxNumber) && (
                        <Box>
                          <Button
                            size="small"
                            sx={{ p: 0, minWidth: 0 }}
                            onClick={() => openDocument(formByRx.get(row.rxNumber) as PhoxPatientDocument)}
                          >
                            Vacation supply form on file
                          </Button>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>{shortNumber(row.perDay)}</TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={qtyValue}
                        onChange={(e) => setEdit(row.rxNumber, { kind: 'qty', raw: e.target.value })}
                        inputProps={{ min: 0, 'aria-label': `Qty on hand for Rx ${row.rxNumber}` }}
                        sx={{ width: 96 }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={daysValue}
                        disabled={row.perDay == null}
                        onChange={(e) => setEdit(row.rxNumber, { kind: 'days', raw: e.target.value })}
                        inputProps={{ min: 0, step: 1, 'aria-label': `Days on hand for Rx ${row.rxNumber}` }}
                        sx={{ width: 84 }}
                      />
                    </TableCell>
                    <TableCell>{showDate(row.lastFillDate)}</TableCell>
                    <TableCell>{row.daysSupply ?? '—'}</TableCell>
                    <TableCell>{showDate(row.runsOut)}</TableCell>
                    <TableCell>{showDate(row.earliestRefill)}</TableCell>
                    <TableCell>
                      {statusChip(row)}
                      {row.status === 'cannot_calculate' && row.reason && (
                        <Typography variant="caption" color="text.secondary" component="div">
                          {row.reason}
                        </Typography>
                      )}
                      {row.ruleDescription && (
                        <Typography variant="caption" color="text.secondary" component="div" data-testid={`vacation-rule-${row.rxNumber}`}>
                          {row.ruleDescription}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{row.bridgeQty > 0 ? `${row.bridgeQty} by ${showDate(row.runsOut)}` : '—'}</TableCell>
                    <TableCell>{row.fillQty > 0 ? row.fillQty : '—'}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
        Per day is the last fill's quantity over its days supply. Qty on hand is estimated from the last fill; change it
        if the patient has more or less.
      </Typography>

      {plan.status === 'ok' && fillware && (
        <VacationSupplyFormDialog
          open={requestOpen}
          onClose={() => setRequestOpen(false)}
          pharmacyId={pharmacyId}
          patientId={patientId}
          request={buildVacationFormRequest({ plan, trip })}
          onCreated={(result) => {
            setRequestOpen(false);
            setFormCreated(result);
          }}
        />
      )}
      {plan.status === 'ok' && coverage && !fillware && !recordFailed && !record.isLoading && (
        <VacationSupplyRequestDialog
          open={requestOpen}
          onClose={() => setRequestOpen(false)}
          pharmacyId={pharmacyId}
          buildTask={(department, assignedTo) =>
            buildVacationTask({
              plan,
              trip,
              coverage: {
                value: coverage,
                source: coverageDefault.source,
                isDefault: coverageChoice == null || coverageChoice === coverageDefault.coverage,
              },
              patientId,
              patientName,
              department,
              assignedTo,
            })
          }
          onCreated={(title) => {
            setRequestOpen(false);
            setCreated(title);
          }}
        />
      )}
    </Box>
  );
};

export default VacationSupplyView;
